import { useState, useEffect, useCallback, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { oppositeSide, sidepanelSideSign, type SidepanelSide } from '@shared/sidepanel/side'
import { useDragCursor } from '../../../hooks/use-drag-cursor'
import {
  RESIZE_HANDLE_INSIDE_PX,
  RESIZE_HANDLE_OUTSIDE_PX,
  RESIZE_HANDLE_SIZE_PX,
  SURFACE_INSET_PX,
  SURFACE_RADIUS_INSIDE,
  RESIZE_PREVIEW_CLASS,
} from './resize-handle-metrics'

interface ResizeHandleProps {
  sidepanelWidth: number
  defaultWidth: number
  clampWidth: (width: number) => number
  onResizeComplete: (newWidth: number) => void
  side: SidepanelSide
}

export function ResizeHandle({ sidepanelWidth, defaultWidth, clampWidth, onResizeComplete, side }: ResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [showOverlay, setShowOverlay] = useState(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)
  const previewWidthRef = useRef(0)
  const previewLineRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)
  const listenersRef = useRef<{
    move: (e: PointerEvent) => void
    up: () => void
  } | null>(null)

  // The handle sits on the panel's inner edge (away from the screen edge), so
  // its CSS anchor and drag direction flip with the panel side.
  const sideSign = sidepanelSideSign(side)
  const innerEdge = oppositeSide(side)

  const positionPreviewLine = useCallback(
    (offset: number) => {
      if (!previewLineRef.current) {
        return
      }
      previewLineRef.current.style[innerEdge] = `calc(var(--sidepanel-left-padding, 12px) - ${offset}px)`
    },
    [innerEdge],
  )

  const cleanupListeners = useCallback(() => {
    if (listenersRef.current) {
      document.removeEventListener('pointermove', listenersRef.current.move)
      document.removeEventListener('pointerup', listenersRef.current.up)
      listenersRef.current = null
    }
  }, [])

  // Cleanup listeners on unmount (e.g. sidepanel closes mid-drag)
  useEffect(() => {
    return () => {
      cleanupListeners()
    }
  }, [cleanupListeners])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation()
      e.preventDefault()
      handleRef.current?.setPointerCapture(e.pointerId)
      startXRef.current = e.clientX
      startWidthRef.current = sidepanelWidth
      previewWidthRef.current = sidepanelWidth
      setIsDragging(true)

      cleanupListeners()

      const onPointerMove = (ev: PointerEvent) => {
        // Dragging away from the screen edge widens the panel on either side.
        const delta = (startXRef.current - ev.clientX) * sideSign
        const newWidth = clampWidth(startWidthRef.current + delta)
        previewWidthRef.current = newWidth
        positionPreviewLine(newWidth - startWidthRef.current)
      }

      const onPointerUp = () => {
        cleanupListeners()
        const finalWidth = previewWidthRef.current
        setShowOverlay(false)
        setIsDragging(false)
        onResizeComplete(finalWidth)
      }

      listenersRef.current = { move: onPointerMove, up: onPointerUp }
      document.addEventListener('pointermove', onPointerMove)
      document.addEventListener('pointerup', onPointerUp)
    },
    [sidepanelWidth, clampWidth, onResizeComplete, cleanupListeners, sideSign, positionPreviewLine],
  )

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      cleanupListeners()
      setShowOverlay(false)
      setIsDragging(false)
      onResizeComplete(defaultWidth)
    },
    [onResizeComplete, defaultWidth, cleanupListeners],
  )

  useEffect(() => {
    if (!isDragging) {
      setShowOverlay(false)
      return
    }
    const timer = setTimeout(() => setShowOverlay(true), 200)
    return () => clearTimeout(timer)
  }, [isDragging])

  // Sync overlay position on first render (catches drag delta accumulated during 200ms delay)
  useEffect(() => {
    if (!showOverlay || !previewLineRef.current) return
    positionPreviewLine(previewWidthRef.current - startWidthRef.current)
  }, [showOverlay, positionPreviewLine])

  useDragCursor(isDragging, 'ew-resize')

  // Apply brightness filter to sidepanel content during drag
  useEffect(() => {
    if (!showOverlay) return
    const content = document.querySelector<HTMLElement>('[data-sidepanel-content]')
    if (content) {
      content.style.filter = 'brightness(0.75)'
      content.style.transition = 'filter 0.15s ease'
    }
    return () => {
      if (content) {
        content.style.filter = ''
        content.style.transition = ''
      }
    }
  }, [showOverlay])

  return (
    <>
      {/* Hit area — 4px into the gutter, 2px into the panel. Stops short of the
          corner handles so diagonal resize owns those regions. */}
      <div
        ref={handleRef}
        onPointerDown={handlePointerDown}
        onDoubleClick={handleDoubleClick}
        /* z-40 keeps the resize handle above the view router (z-6) so the panel
           stays resizable while a View is open. */
        className="group pointer-events-auto absolute z-40 cursor-ew-resize"
        style={{
          [innerEdge]: `calc(var(--sidepanel-left-padding, 12px) - ${RESIZE_HANDLE_OUTSIDE_PX}px)`,
          width: RESIZE_HANDLE_SIZE_PX,
          top: SURFACE_INSET_PX + RESIZE_HANDLE_INSIDE_PX,
          bottom: SURFACE_INSET_PX + RESIZE_HANDLE_INSIDE_PX,
        }}
      >
        {/* Hover line indicator — pinned to the surface edge (not the strip's
            midline, which is asymmetric), spanning the straight part of the
            card edge between the corner handles. */}
        <div
          className={twMerge(
            'border-on-light absolute w-0.5 rounded-full border drop-shadow-md transition-opacity duration-150 ease-out',
            isDragging ? 'opacity-0' : 'bg-white-8 opacity-0 group-hover:opacity-100 group-hover:delay-150',
          )}
          style={{
            [innerEdge]: RESIZE_HANDLE_OUTSIDE_PX,
            // Anchor edge is the surface; flip the shift so the 2px line centers on it.
            transform: innerEdge === 'left' ? 'translateX(-50%)' : 'translateX(50%)',
            top: SURFACE_RADIUS_INSIDE,
            bottom: SURFACE_RADIUS_INSIDE,
          }}
        />
      </div>

      {showOverlay && (
        /* Traces the surface card exactly — same 12px gutter, same 16px radius —
           so the preview reads as the panel's new silhouette. The panel root is
           already dock-aware (see `useSidepanelVerticalLayout`), so the bottom
           inset must not subtract the dock a second time. */
        <div
          ref={previewLineRef}
          className={RESIZE_PREVIEW_CLASS}
          style={{
            [innerEdge]: 'var(--sidepanel-left-padding, 12px)',
            [side]: SURFACE_INSET_PX,
            top: SURFACE_INSET_PX,
            bottom: SURFACE_INSET_PX,
          }}
        />
      )}
    </>
  )
}
